/**
 * Settings → Voice providers → Advanced Engine → Voice agents.
 *
 * Nested inside VoiceProvidersPanel's Advanced-engine branch (it used to be
 * its own top-level settings tab) — agent credentials only matter once a
 * dealership has switched to the Advanced engine, so putting them a click
 * away under a separate tab just meant a second stop to configure one thing.
 *
 * The vendor agents this dealership runs. A tenant may have several live at
 * once — Retell on the sales line, Vapi on service — each with its own
 * credential and its own MCP tool key.
 *
 * **There is no routing to configure here.** Each agent answers its own SIP
 * trunk and phone numbers, so the vendor picks up the call before this backend
 * hears about it. `Active` is a kill switch: turning an agent off stops
 * accepting its webhooks and tool calls, it does not send calls elsewhere.
 *
 * Secret lifecycle, matching the backend:
 *   apiKey null / omitted on update  → keep the stored credential
 *   apiKey "sk-..."                  → replace it, and re-verify with the vendor
 *   toolKey                          → returned ONCE, by create and rotate
 *
 * Rotating pushes the new key straight into the vendor (MCP header + webhook
 * token) where the vendor supports it, and nothing is shown. Only if that push
 * is partial or fails is the key shown once in the banner for a manual paste.
 * Only its SHA-256 is stored, so a lost key cannot be recovered — rotate again.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTime } from '../../lib/datetime';
import { voiceAgentsService } from '../../lib/services';
import RetellVoiceControl from './RetellVoiceControl';
import './VoiceAgents.css';

/* ────────────────────────────── shared bits ────────────────────────────── */

const input = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-primary)',
  background: 'var(--bg-glass)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
};

const label = { fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

function Field({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={label}>{title}</label>
      {children}
      {hint && <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StatusPill({ status, isActive }) {
  // Inactive wins visually: a verified-but-switched-off agent is not serving
  // anything, and showing it green would be misleading.
  if (isActive === false) return <span className="pill filled">Off</span>;
  const map = {
    ACTIVE:   ['Verified',     'success'],
    PENDING:  ['Not verified', 'warning'],
    ERROR:    ['Rejected',     'danger'],
    DISABLED: ['Disabled',     ''],
  };
  const [text, color] = map[status] || [status || '—', ''];
  return <span className={`pill ${color} filled`}>{text}</span>;
}

/** The plaintext tool key, shown once. Deliberately loud — it cannot be re-read. */
function ToolKeyBanner({ toolKey, providerKey, detail, onDismiss }) {
  const [copied, setCopied] = useState(false);
  if (!toolKey) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(toolKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked on insecure origins and in some browsers; the key
      // is selectable on screen either way, so this is not worth an error.
      setCopied(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--warning)',
        background: 'var(--warning-bg)',
        borderRadius: 'var(--radius-sm)',
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        Copy this tool key now — it is shown once
      </div>
      <div className="text-xs text-muted" style={{ marginBottom: 8 }}>
        Only a hash of it is stored, so it cannot be shown again. Paste it into the
        agent&apos;s MCP server configuration in the {providerKey || 'vendor'} dashboard
        as the <code>X-Tool-Key</code> header, and as the <code>token</code> query
        parameter on its webhook URL.
      </div>
      {detail && (
        <div className="text-xs" style={{ marginBottom: 8, color: 'var(--danger)' }}>
          Automatic update to {providerKey || 'the vendor'} did not complete: {detail}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input readOnly value={toolKey} style={{ ...input, flex: 1 }} onFocus={(e) => e.target.select()} />
        <button type="button" className="btn small" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn small" onClick={onDismiss}>Done</button>
      </div>
    </div>
  );
}

/* ──────────────────────────────── the panel ─────────────────────────────── */

const EMPTY_FORM = { providerKey: '', agentId: '', agentName: '', apiKey: '', apiBase: '' };

/**
 * The prompt an agent is running — read live from the vendor, and editable.
 *
 * Nothing is cached on our side: what is shown is what the vendor holds right
 * now, including edits the dealer made in their own dashboard.
 *
 * Saving is guarded rather than blind. The text loaded into the editor is sent
 * back as `expectedCurrent`; if the live prompt moved in the meantime the
 * backend refuses with 409 and hands back what it found, so a dashboard edit is
 * never destroyed without someone seeing it first. `force` is the operator
 * having looked and chosen anyway.
 *
 * Success is not always success: `effective:false` means the vendor accepted
 * the write but live calls will not use it yet. That renders as a warning, not
 * a tick — a green tick over a prompt that is not live is the one outcome this
 * screen must never produce.
 */
function AgentPrompt({ agentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // draft is what is in the box; loaded is what the vendor gave us. The
  // difference drives both the dirty marker and the conflict check.
  const [draft, setDraft] = useState('');
  const [loaded, setLoaded] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState(null);   // { kind: 'ok'|'warn', text }
  const [conflict, setConflict] = useState(null);     // vendor's current prompt

  const applyFetched = useCallback((d) => {
    setData(d);
    setDraft(d?.prompt || '');
    setLoaded(d?.prompt || '');
  }, []);

  const fetchPrompt = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaveState(null);
    setConflict(null);
    try {
      applyFetched(await voiceAgentsService.getPrompt(agentId));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not read the prompt from the provider');
    } finally {
      setLoading(false);
    }
  }, [agentId, applyFetched]);

  // Lazy: mounted only when the operator opens the Prompt section, so this is
  // one vendor round-trip per agent actually looked at.
  useEffect(() => {
    if (!data && !loading && !error) fetchPrompt();
  }, [data, loading, error, fetchPrompt]);

  const dirty = draft !== loaded;
  // custom-llm agents keep their prompt on the dealer's own server; there is
  // nothing at the vendor to write to.
  const editable = Boolean(data) && data.engineType !== 'custom-llm' && data.prompt !== null;

  const save = async (force = false) => {
    setSaving(true);
    setError(null);
    setSaveState(null);
    try {
      const res = await voiceAgentsService.updatePrompt(agentId, {
        prompt: draft,
        expectedCurrent: loaded,
        force,
      });
      setConflict(null);
      setLoaded(draft);
      setSaveState(res.effective
        ? { kind: 'ok', text: 'Saved. Live calls will use this prompt.' }
        : { kind: 'warn', text: res.warning || 'Saved, but it may not be live yet.' });
    } catch (e) {
      if (e?.response?.status === 409) {
        // Someone edited it in the vendor dashboard since we loaded.
        setConflict(e.response.data?.current ?? e.response.data?.data?.current ?? '');
      } else {
        setError(e?.response?.data?.message || e.message || 'Could not save the prompt');
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToRecommended = async () => {
    setError(null);
    try {
      const res = await voiceAgentsService.recommendedPrompt();
      // Fills the box only — the operator still has to press Save.
      setDraft(res?.prompt || '');
      setSaveState(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load the recommended prompt');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the text is selectable anyway */
    }
  };

  const box = {
    width: '100%',
    minHeight: 220,
    maxHeight: 420,
    padding: 10,
    fontSize: 12,
    fontFamily: 'var(--font-mono)',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    background: 'var(--bg-glass)',
    border: `1px solid ${dirty ? 'var(--warning)' : 'var(--border-color)'}`,
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    outline: 'none',
    resize: 'vertical',
  };

  const meta = [data?.engineType, data?.model].filter(Boolean).join(' · ');

  return (
    <div>
      <div className="va-panel-head">
        <div>
          <div className="va-panel-title">Prompt</div>
          <div className="va-panel-sub">
            {meta ? <span className="mono">{meta}</span> : 'What the agent is told to do on every call.'}
          </div>
        </div>
        <div className="va-actions">
          {dirty && <span className="pill warning filled">Unsaved</span>}
          {draft && (
            <button type="button" className="btn small" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
          <button type="button" className="btn small" disabled={loading || saving} onClick={fetchPrompt}>
            {loading ? 'Reading…' : 'Reload'}
          </button>
          {editable && (
            <>
              <button type="button" className="btn small" disabled={saving} onClick={resetToRecommended}>
                Reset to recommended
              </button>
              <button
                type="button"
                className="btn small primary"
                disabled={saving || !dirty}
                onClick={() => save(false)}
              >
                {saving ? 'Saving…' : 'Save to provider'}
              </button>
            </>
          )}
        </div>
      </div>

      <div>
          {loading && <div className="text-xs text-muted">Reading the prompt from the provider…</div>}

          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}

          {saveState && (
            <div
              className="text-xs"
              style={{
                marginBottom: 6,
                color: saveState.kind === 'ok' ? 'var(--success)' : 'var(--warning)',
              }}
            >
              {saveState.text}
            </div>
          )}

          {/* A refused overwrite. Show what the vendor holds now so the operator
              can decide, rather than silently winning or silently losing. */}
          {conflict !== null && (
            <div
              style={{
                marginBottom: 8,
                padding: 10,
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div className="text-xs" style={{ color: 'var(--warning)', marginBottom: 6 }}>
                This prompt was changed in the provider&apos;s dashboard after you opened it.
                Saving now would replace that change.
              </div>
              <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
                What the provider holds right now:
              </div>
              <pre
                className="mono text-xs"
                style={{
                  margin: 0, padding: 8, maxHeight: 160, overflow: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {conflict || '(empty)'}
              </pre>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn small danger" disabled={saving} onClick={() => save(true)}>
                  Overwrite anyway
                </button>
                <button
                  type="button"
                  className="btn small"
                  disabled={saving}
                  onClick={() => { setDraft(conflict || ''); setLoaded(conflict || ''); setConflict(null); }}
                >
                  Discard mine, keep theirs
                </button>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {data.beginMessage && (
                <div className="text-xs text-muted" style={{ marginBottom: 6 }}>
                  Opens with: <i>{data.beginMessage}</i>
                </div>
              )}

              {/* These answer the question asked straight after "what is the
                  prompt?" — is this agent wired to our MCP server, and has it
                  picked up our tools? */}
              {Array.isArray(data.mcpUrls) && data.mcpUrls.length > 0 && (
                <div className="text-xs text-muted" style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                  MCP servers: {data.mcpUrls.map((u) => (
                    <span key={u} className="mono" style={{ marginRight: 8 }}>{u}</span>
                  ))}
                </div>
              )}

              {Array.isArray(data.toolNames) && data.toolNames.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                  {data.toolNames.map((t) => <span key={t} className="pill">{t}</span>)}
                </div>
              )}

              {data.note && (
                <div className="text-xs" style={{ color: 'var(--warning)', marginBottom: 6 }}>
                  {data.note}
                </div>
              )}

              {editable ? (
                <>
                  <textarea
                    style={box}
                    value={draft}
                    spellCheck={false}
                    onChange={(e) => setDraft(e.target.value)}
                    aria-label="Agent prompt"
                  />
                  <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                    Saving writes straight to the provider and changes what the agent says on live
                    calls. {'{{double-brace}}'} placeholders are per-call variables — leave them in place.
                  </div>
                </>
              ) : (
                data.prompt ? (
                  <pre
                    className="mono text-xs"
                    style={{
                      margin: 0, padding: 10, maxHeight: 320, overflow: 'auto',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                    }}
                  >
                    {data.prompt}
                  </pre>
                ) : (
                  !data.note && (
                    <div className="text-xs text-muted">
                      The provider returned no prompt for this agent.
                    </div>
                  )
                )
              )}
            </>
          )}
      </div>
    </div>
  );
}

/**
 * The sections under each agent card, as tabs: Prompt, and — for Retell —
 * Voice & behaviour (voice, speed, stability, volume). Collapsed by default;
 * clicking the open tab closes it. A section is mounted the first time it is
 * opened (so listing agents makes no vendor calls) and then only hidden, so
 * switching tabs never throws away an unsaved prompt edit.
 */
function AgentSections({ agent, autoOpenPrompt }) {
  const sections = [
    { key: 'prompt', label: 'Prompt' },
    ...(agent.providerKey === 'RETELL' ? [{ key: 'voice', label: 'Voice & behaviour' }] : []),
  ];
  const [active, setActive] = useState(autoOpenPrompt ? 'prompt' : null);
  const [opened, setOpened] = useState(() => new Set(autoOpenPrompt ? ['prompt'] : []));

  const toggle = (key) => {
    setActive((cur) => (cur === key ? null : key));
    setOpened((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  return (
    <div className="va-sections">
      <div className="va-tabs" role="tablist">
        {sections.map((sec) => (
          <button
            key={sec.key}
            type="button"
            role="tab"
            aria-selected={active === sec.key}
            aria-expanded={active === sec.key}
            className={`va-tab ${active === sec.key ? 'active' : ''}`}
            onClick={() => toggle(sec.key)}
          >
            {sec.label}
            <span className="va-tab-chevron" aria-hidden="true">▾</span>
          </button>
        ))}
      </div>
      {opened.has('prompt') && (
        <div className="va-panel" role="tabpanel" hidden={active !== 'prompt'}>
          <AgentPrompt agentId={agent.id} />
        </div>
      )}
      {opened.has('voice') && (
        <div className="va-panel" role="tabpanel" hidden={active !== 'voice'}>
          <RetellVoiceControl agentId={agent.id} />
        </div>
      )}
    </div>
  );
}

export default function VoiceAgentsPanel({ onAgentsChange } = {}) {
  const [catalog, setCatalog] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);          // id or 'create', so one row's spinner doesn't freeze the rest
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [freshKey, setFreshKey] = useState(null);  // { toolKey, providerKey, detail }
  const [syncedNote, setSyncedNote] = useState(null); // shown when the vendor was updated automatically
  const [testResults, setTestResults] = useState({});
  // The add form lives in a modal rather than permanently below the list:
  // registering an agent is a once-in-a-while act, and an always-open
  // five-field credential form pushed the agents themselves off the screen.
  const [showAdd, setShowAdd] = useState(false);
  // The agent registered a moment ago. Its prompt section opens by itself —
  // "I added the agent, show me what it is running" is the whole reason an
  // operator is on this screen at that instant.
  const [justAddedId, setJustAddedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, list] = await Promise.all([
        voiceAgentsService.catalog(),
        voiceAgentsService.list(),
      ]);
      setCatalog(Array.isArray(cat) ? cat : []);
      setAgents(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load voice agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Escape closes the add dialog, matching every other modal in the portal.
  useEffect(() => {
    if (!showAdd) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowAdd(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAdd]);

  // Nested inside VoiceProvidersPanel's Advanced-engine section, which needs
  // a live count for its own "N agents configured" / mic-test gating — every
  // create/toggle/rotate/remove above ends in `load()`, so watching `agents`
  // catches all of them without threading a callback through each one.
  useEffect(() => { onAgentsChange?.(agents); }, [agents, onAgentsChange]);

  /** Only vendors with a working client implementation can be selected. */
  const offerable = useMemo(() => catalog.filter((p) => p.isEnabled), [catalog]);

  /**
   * The provider actually shown in the select.
   *
   * Derived rather than pushed into state by an effect: with exactly one
   * offerable vendor there is nothing to choose, so defaulting to it during
   * render avoids a second render pass — and the cascading render the
   * `set-state-in-effect` rule warns about. The submit handler reads this same
   * value, so what is displayed is what gets sent.
   */
  const providerKey = form.providerKey || (offerable.length === 1 ? offerable[0].providerKey : '');

  const selected = useMemo(
    () => catalog.find((p) => p.providerKey === providerKey) || null,
    [catalog, providerKey],
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!providerKey || !form.agentId.trim() || !form.agentName.trim() || !form.apiKey.trim()) {
      setFormError('Provider, agent id, name and API key are all required.');
      return;
    }
    setBusy('create');
    try {
      const created = await voiceAgentsService.create({
        providerKey,
        agentId: form.agentId.trim(),
        agentName: form.agentName.trim(),
        apiKey: form.apiKey.trim(),
        apiBase: form.apiBase.trim() || null,
      });
      if (created?.toolKey) setFreshKey({ toolKey: created.toolKey, providerKey: created.providerKey });
      if (created?.id) setJustAddedId(created.id);
      // Keep the provider selected — adding several agents for one vendor is
      // the common case.
      setForm({ ...EMPTY_FORM, providerKey });
      // Only on success: a failed submit keeps the dialog (and its error)
      // open so the operator can correct the field rather than re-typing
      // five of them. The tool key banner it reveals is in the panel behind.
      setShowAdd(false);
      await load();
    } catch (e2) {
      setFormError(e2?.response?.data?.message || e2.message || 'Could not register the agent');
    } finally {
      setBusy(null);
    }
  };

  const act = async (id, fn) => {
    setBusy(id);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'That action failed');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const toggle = (a) => act(a.id, async () => { await voiceAgentsService.toggle(a.id); await load(); });

  const rotate = (a) => act(a.id, async () => {
    const res = await voiceAgentsService.rotateToolKey(a.id);
    if (res?.toolKey) {
      setSyncedNote(null);
      setFreshKey({ toolKey: res.toolKey, providerKey: a.providerKey, detail: res.syncDetail });
    } else if (res?.syncStatus === 'SYNCED') {
      setFreshKey(null);
      setSyncedNote(`Key rotated and updated in ${a.providerKey} (MCP header and webhook token). Nothing to paste.`);
    }
    await load();
  });

  const test = (a) => act(a.id, async () => {
    const res = await voiceAgentsService.test(a.id);
    setTestResults((prev) => ({ ...prev, [a.id]: res }));
    await load();   // status / lastError / verifiedAt may all have moved
  });

  const remove = (a) => {
    // Deleting also clears the agent's master-directory row, which is what its
    // webhooks and tool calls resolve through — worth stating plainly.
    const ok = window.confirm(
      `Remove ${a.agentName} (${a.agentId})?\n\n`
      + 'Its tool key stops working immediately and its webhooks will be refused. '
      + 'Bookings it already created are not affected.',
    );
    if (!ok) return;
    act(a.id, async () => { await voiceAgentsService.remove(a.id); await load(); });
  };

  if (loading) return <div className="text-sm text-muted">Loading voice agents…</div>;

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: 16, gap: 12, alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Voice agents</h2>
          <p className="text-sm text-muted" style={{ maxWidth: 620 }}>
            The vendor agents that answer this dealership&apos;s calls. Several can be
            live at once — each answers its own phone numbers, so there is nothing to
            route here. Credentials are stored encrypted and never shown again.
          </p>
        </div>
        <button
          type="button"
          className="btn small primary"
          style={{ flexShrink: 0 }}
          onClick={() => { setFormError(null); setShowAdd(true); }}
        >
          ＋ Add agent
        </button>
      </div>

      {error && (
        <div className="text-sm" style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div>
      )}

      {syncedNote && (
        <div className="text-sm" role="status"
             style={{ color: 'var(--success)', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{syncedNote}</span>
          <button type="button" className="btn small" onClick={() => setSyncedNote(null)}>Dismiss</button>
        </div>
      )}

      <ToolKeyBanner
        toolKey={freshKey?.toolKey}
        providerKey={freshKey?.providerKey}
        detail={freshKey?.detail}
        onDismiss={() => setFreshKey(null)}
      />

      {/* ─────────────────────── configured agents ─────────────────────── */}
      {agents.length === 0 ? (
        <div className="text-sm text-muted" style={{ marginBottom: 20 }}>
          No agents configured yet — use <b>Add agent</b> above to register one and get
          its MCP tool key.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {agents.map((a) => {
            const rowBusy = busy === a.id;
            const result = testResults[a.id];
            return (
              <div
                key={a.id}
                className="card"
                style={{ padding: 12, opacity: a.isActive === false ? 0.65 : 1 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{a.agentName}</span>
                      <span className="pill">{a.providerKey}</span>
                      <StatusPill status={a.status} isActive={a.isActive} />
                    </div>
                    <div className="mono text-xs text-muted" style={{ wordBreak: 'break-all' }}>
                      {a.agentId}
                    </div>
                    {a.apiBase && (
                      <div className="mono text-xs text-muted">{a.apiBase}</div>
                    )}
                    <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                      {a.hasApiKey ? 'Credential stored' : 'No credential'}
                      {a.verifiedAt && ` · verified ${formatDateTime(a.verifiedAt)}`}
                    </div>
                    {a.lastError && (
                      <div className="text-xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                        {a.lastError}
                      </div>
                    )}
                    {result && (
                      <div
                        className="text-xs"
                        style={{ marginTop: 4, color: result.verified ? 'var(--success)' : 'var(--danger)' }}
                      >
                        {result.verified ? 'Vendor confirmed this agent' : result.detail || 'Not verified'}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="button" className="btn small" disabled={rowBusy} onClick={() => test(a)}>
                      Test
                    </button>
                    <button type="button" className="btn small" disabled={rowBusy} onClick={() => toggle(a)}>
                      {a.isActive === false ? 'Turn on' : 'Turn off'}
                    </button>
                    <button type="button" className="btn small" disabled={rowBusy} onClick={() => rotate(a)}>
                      Rotate key
                    </button>
                    <button type="button" className="btn small danger" disabled={rowBusy} onClick={() => remove(a)}>
                      Remove
                    </button>
                  </div>
                </div>

                <AgentSections agent={a} autoOpenPrompt={a.id === justAddedId} />
              </div>
            );
          })}
        </div>
      )}

      {/* ──────────────────────── add-agent dialog ──────────────────────── */}
      {showAdd && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-agent-title"
        className="modal-backdrop"
        onClick={() => setShowAdd(false)}
      >
      <form className="modal" onSubmit={create} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div id="add-agent-title" style={{ fontSize: 16, fontWeight: 600 }}>Add an agent</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              We verify the credential with the vendor before activating the agent.
            </div>
          </div>
          <button type="button" className="modal-close" onClick={() => setShowAdd(false)} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
        <Field
          title="Provider"
          hint={
            catalog.length > offerable.length
              ? 'Greyed-out vendors are in the catalogue but have no client implementation yet.'
              : undefined
          }
        >
          <select style={input} value={providerKey} onChange={set('providerKey')}>
            <option value="">— choose a provider —</option>
            {catalog.map((p) => (
              <option key={p.providerKey} value={p.providerKey} disabled={!p.isEnabled}>
                {p.displayName}{p.isEnabled ? '' : ' (not available yet)'}
              </option>
            ))}
          </select>
        </Field>

        {/* Labels follow the vendor's own wording: Retell says "Agent", Vapi
            says "Assistant". Falls back to "Agent" before one is chosen. */}
        <Field
          title={`${selected?.agentIdLabel || 'Agent'} id`}
          hint={
            selected?.agentIdExample
              ? `From your ${selected.displayName} dashboard, e.g. ${selected.agentIdExample}`
              : 'The id the vendor assigned to this agent.'
          }
        >
          <input
            style={input}
            value={form.agentId}
            onChange={set('agentId')}
            placeholder={selected?.agentIdExample || ''}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <Field title="Display name" hint="How this agent appears in the portal. Yours to choose.">
          <input
            style={input}
            value={form.agentName}
            onChange={set('agentName')}
            placeholder="Sales line agent"
            autoComplete="off"
          />
        </Field>

        <Field
          title="API key"
          hint="Your own vendor account key. Encrypted at rest and never shown again — we verify it before activating the agent."
        >
          <input
            type="password"
            style={input}
            value={form.apiKey}
            onChange={set('apiKey')}
            autoComplete="new-password"
            spellCheck={false}
          />
        </Field>

        <Field
          title="API base URL (optional)"
          hint={
            selected?.apiBase
              ? `Leave blank to use ${selected.apiBase}. Set only for a regional endpoint or a mock.`
              : 'Leave blank to use the provider default.'
          }
        >
          <input
            style={input}
            value={form.apiBase}
            onChange={set('apiBase')}
            placeholder={selected?.apiBase || ''}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        {formError && (
          <div className="text-sm" style={{ color: 'var(--danger)' }}>{formError}</div>
        )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={() => setShowAdd(false)}
                  disabled={busy === 'create'}>
            Cancel
          </button>
          <button type="submit" className="btn small primary" disabled={busy === 'create'}>
            {busy === 'create' ? 'Verifying with the provider…' : 'Add agent'}
          </button>
        </div>
      </form>
      </div>
      )}
    </div>
  );
}
